"use client";

/**
 * Live Prices on Sign-In (2026-08-23): the missing-page gate.
 *
 * Mounted alongside the "updating" shell on a saved page that has NO cached data
 * yet (a genuine miss). When the feature is on:
 *   - a SIGNED-OUT visitor gets the existing sign-in popup (the same MioAuthDialog
 *     used for fresh search / watch / negotiate); on sign-in we hand off to a
 *     live search that builds the page.
 *   - a SIGNED-IN visitor is handed straight to a live search to build it (a
 *     genuine miss has no stored targets to refresh, so a full search is the only
 *     way to build it - the pSEO refresh cannot).
 *   - bots / no-JS / not-yet-loaded see only the server "updating" shell, so the
 *     SSR HTML is unchanged and still indexable.
 *
 * Inert while NEXT_PUBLIC_LIVE_PRICES_ENABLED is off (renders null; the page
 * behaves exactly as today).
 */

import { useEffect, useRef, useState } from "react";

import { useRouter } from "next/navigation";
import type { Route } from "next";
import { useUser } from "@clerk/nextjs";

import { MioAuthDialog } from "@/components/auth/MioAuthDialog";
import type { AuthMode } from "@/components/auth/useAuthFlow";
import { LIVE_PRICES_ENABLED } from "@/lib/flags";

interface Props {
  /** The raw query for the live-search build handoff. */
  query: string;
}

export function MissingPageGate({ query }: Props) {
  const { isSignedIn, isLoaded } = useUser();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<AuthMode>("sign-up");
  const startedRef = useRef(false);

  const q = (query ?? "").trim();

  function handoff() {
    // A genuine miss has no stored targets, so build it with a full live search
    // (the same handoff StaleCeilingRefresh uses: from_chip=1 skips clarify,
    // r=<nonce> skips the resolve->/c bounce so the search runs fresh).
    const nonce = Date.now().toString(36);
    const url = `/compare?q=${encodeURIComponent(q)}&from_chip=1&r=${nonce}`;
    router.replace(url as Route);
  }

  useEffect(() => {
    if (!LIVE_PRICES_ENABLED || !isLoaded || !q) return;
    if (isSignedIn) {
      if (!startedRef.current) {
        startedRef.current = true;
        handoff();
      }
    } else {
      setOpen(true);
    }
    // handoff is a stable closure over q/router; deps below cover its inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, q, router]);

  function handleAuthenticated() {
    startedRef.current = true;
    handoff();
  }

  if (!LIVE_PRICES_ENABLED) return null;

  return (
    <MioAuthDialog
      open={open}
      mode={mode}
      onOpenChange={setOpen}
      onModeChange={setMode}
      onAuthenticated={handleAuthenticated}
    />
  );
}
